/**
 * Admin Audit Log Page
 *
 * Global audit log viewer for all admin actions.
 * Superadmin only.
 */

import { useState } from 'react';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  Search,
  ChevronDown,
  ChevronRight,
  User,
  Shield,
  Building2,
  FileText,
  Settings,
  Play,
  Download,
  Calendar,
  FileDown,
} from 'lucide-react';
import { AuditLogEntry, AdminActionType } from '@/integrations/firebase/types';
import { format } from 'date-fns';

// Audit logs - will be loaded from Firestore
// TODO: Replace with real Firestore query for global audit logs
const auditLogs: (AuditLogEntry & { targetOrgName?: string })[] = [];

const ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  CREATE_ORG: Building2,
  UPDATE_ORG: Building2,
  DELETE_ORG: Building2,
  SUSPEND_ORG: Building2,
  ACTIVATE_ORG: Building2,
  INVITE_USER: User,
  DISABLE_USER: User,
  ENABLE_USER: User,
  CHANGE_USER_ROLE: User,
  CHANGE_ADMIN_ROLE: Shield,
  ADD_ADMIN_USER: Shield,
  REMOVE_ADMIN_USER: Shield,
  SET_PLAN: Settings,
  SET_SCOPE: Settings,
  IMPERSONATE_ORG: Shield,
  EXIT_IMPERSONATION: Shield,
  CREATE_JOB_AS_ORG: FileText,
  UPLOAD_DOC_AS_ORG: FileText,
  RUN_CHECK_AS_ORG: Play,
  RERUN_CHECK: Play,
  DOWNLOAD_DEBUG_BUNDLE: Download,
};

const ACTION_COLORS: Record<string, string> = {
  CREATE_ORG: 'text-green-400',
  UPDATE_ORG: 'text-blue-400',
  DELETE_ORG: 'text-red-400',
  SUSPEND_ORG: 'text-red-400',
  ACTIVATE_ORG: 'text-green-400',
  INVITE_USER: 'text-green-400',
  DISABLE_USER: 'text-red-400',
  ENABLE_USER: 'text-green-400',
  SET_PLAN: 'text-purple-400',
  SET_SCOPE: 'text-purple-400',
  IMPERSONATE_ORG: 'text-amber-400',
  EXIT_IMPERSONATION: 'text-amber-400',
  CREATE_JOB_AS_ORG: 'text-blue-400',
  UPLOAD_DOC_AS_ORG: 'text-blue-400',
  RUN_CHECK_AS_ORG: 'text-blue-400',
  CHANGE_ADMIN_ROLE: 'text-purple-400',
  DOWNLOAD_DEBUG_BUNDLE: 'text-gray-400',
};

const ACTION_CATEGORIES = {
  all: 'All Actions',
  org: 'Organization',
  user: 'User Management',
  support: 'Support Mode',
  compliance: 'Compliance',
  admin: 'Admin Management',
};

const ACTION_CATEGORY_MAP: Record<AdminActionType, keyof typeof ACTION_CATEGORIES> = {
  CREATE_ORG: 'org',
  UPDATE_ORG: 'org',
  DELETE_ORG: 'org',
  SUSPEND_ORG: 'org',
  ACTIVATE_ORG: 'org',
  INVITE_USER: 'user',
  DISABLE_USER: 'user',
  ENABLE_USER: 'user',
  CHANGE_USER_ROLE: 'user',
  RESEND_INVITE: 'user',
  RESET_PASSWORD: 'user',
  SET_PLAN: 'org',
  SET_SCOPE: 'org',
  IMPERSONATE_ORG: 'support',
  EXIT_IMPERSONATION: 'support',
  CREATE_JOB_AS_ORG: 'support',
  UPLOAD_DOC_AS_ORG: 'support',
  RUN_CHECK_AS_ORG: 'compliance',
  RERUN_CHECK: 'compliance',
  DOWNLOAD_DEBUG_BUNDLE: 'compliance',
  CHANGE_ADMIN_ROLE: 'admin',
  ADD_ADMIN_USER: 'admin',
  REMOVE_ADMIN_USER: 'admin',
};

export default function AdminAuditLog() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  // Filter logs
  const filteredLogs = auditLogs.filter((log) => {
    const matchesSearch =
      log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.actorEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.targetOrgName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === 'all' ||
      ACTION_CATEGORY_MAP[log.actionType] === categoryFilter;
    const matchesRole =
      roleFilter === 'all' || log.actorRole === roleFilter;
    return matchesSearch && matchesCategory && matchesRole;
  });

  const toggleExpanded = (logId: string) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getActionIcon = (actionType: AdminActionType) => {
    const Icon = ACTION_ICONS[actionType] || FileText;
    const color = ACTION_COLORS[actionType] || 'text-gray-400';
    return <Icon className={`h-4 w-4 ${color}`} />;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'superadmin':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/40">
            Superadmin
          </Badge>
        );
      case 'admin':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40">
            Admin
          </Badge>
        );
      case 'support':
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">
            Support
          </Badge>
        );
      case 'dev':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/40">
            Dev
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="border-gray-600 text-gray-400">
            {role}
          </Badge>
        );
    }
  };

  const handleExport = () => {
    toast({
      title: 'Not Implemented',
      description: 'Audit log export will be wired to backend',
    });
  };

  // Stats
  const todayLogs = auditLogs.filter(
    (log) =>
      log.timestamp &&
      log.timestamp.toDate().toDateString() === new Date().toDateString()
  ).length;
  const supportModeLogs = auditLogs.filter(
    (log) =>
      log.actionType === 'IMPERSONATE_ORG' ||
      log.actionType === 'CREATE_JOB_AS_ORG' ||
      log.actionType === 'RUN_CHECK_AS_ORG'
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Audit Log</h1>
          <p className="text-gray-400 mt-1">
            Complete history of all admin actions
          </p>
        </div>

        <Button
          onClick={handleExport}
          variant="outline"
          className="border-[hsl(220,15%,25%)] text-gray-300 hover:bg-[hsl(220,15%,15%)] hover:text-white"
        >
          <FileDown className="h-4 w-4 mr-2" />
          Export Log
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[hsl(220,70%,50%)]/20 flex items-center justify-center">
              <FileText className="h-5 w-5 text-[hsl(220,70%,50%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">
                {auditLogs.length}
              </p>
              <p className="text-sm text-gray-400">Total Entries</p>
            </div>
          </div>
        </AdminCard>

        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{todayLogs}</p>
              <p className="text-sm text-gray-400">Today</p>
            </div>
          </div>
        </AdminCard>

        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">
                {supportModeLogs}
              </p>
              <p className="text-sm text-gray-400">Support Actions</p>
            </div>
          </div>
        </AdminCard>
      </div>

      {/* Filters */}
      <AdminCard>
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by description, actor, or org..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
            />
          </div>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-44 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
              {Object.entries(ACTION_CATEGORIES).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
              <SelectValue placeholder="Actor Role" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="superadmin">Superadmin</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="support">Support</SelectItem>
              <SelectItem value="dev">Dev</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </AdminCard>

      {/* Audit Log List */}
      <div className="space-y-2">
        {filteredLogs.length === 0 ? (
          <AdminCard>
            <p className="text-center text-gray-500 py-8">
              No audit log entries found
            </p>
          </AdminCard>
        ) : (
          filteredLogs.map((log) => (
            <Collapsible
              key={log.id}
              open={expandedLogs.has(log.id)}
              onOpenChange={() => toggleExpanded(log.id)}
            >
              <AdminCard className="p-0">
                <CollapsibleTrigger asChild>
                  <button className="w-full p-4 flex items-center gap-4 text-left hover:bg-[hsl(220,15%,12%)] transition-colors">
                    <div className="h-8 w-8 rounded-lg bg-[hsl(220,15%,15%)] flex items-center justify-center">
                      {getActionIcon(log.actionType)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">
                        {log.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {log.actorEmail}
                        </span>
                        {getRoleBadge(log.actorRole)}
                        {log.targetOrgName && (
                          <>
                            <span className="text-xs text-gray-600">→</span>
                            <span className="text-xs text-gray-400">
                              {log.targetOrgName}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-400">
                        {log.timestamp
                          ? format(log.timestamp.toDate(), 'MMM d, h:mm a')
                          : '-'}
                      </p>
                    </div>

                    {log.diff ? (
                      expandedLogs.has(log.id) ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )
                    ) : (
                      <div className="w-4" />
                    )}
                  </button>
                </CollapsibleTrigger>

                {log.diff && (
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0 border-t border-[hsl(220,15%,20%)]">
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-2">Before</p>
                          <pre className="text-xs text-red-400 bg-[hsl(220,15%,12%)] p-3 rounded-md overflow-auto">
                            {JSON.stringify(log.diff.before, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-2">After</p>
                          <pre className="text-xs text-green-400 bg-[hsl(220,15%,12%)] p-3 rounded-md overflow-auto">
                            {JSON.stringify(log.diff.after, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                )}
              </AdminCard>
            </Collapsible>
          ))
        )}
      </div>
    </div>
  );
}
